import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { ChartOfAccountCategory } from '../../../../core/enums/data.enums';
import { AuditEntityType, ModalActions } from '../../../../core/enums/base.enums';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { SortDropdownComponent } from '../../../../parts/sort-dropdown/sort-dropdown/sort-dropdown';
import { AuditLogsComponent } from '../../../../parts/audit-logs/audit-logs.component';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { ChartOfAccountCategorySectionComponent } from '../../components/chart-of-account-category-section/chart-of-account-category-section.component';
import { PageTitleComponent } from '../../../../parts/page-title/page-title.component';

@Component({
	selector: 'ms-chart-of-accounts',
	standalone: true,
	imports: [
		PageTitleComponent,
		CommonModule,
		FormsModule,
		FaDuotoneIconComponent,
		SortDropdownComponent,
		AuditLogsComponent,
		EscCloseDirective,
		ChartOfAccountCategorySectionComponent
	],
	templateUrl: './chart-of-accounts.page.html',
	host: { 'style': 'display:contents' }
})
export class ChartOfAccountsPage {

	//#region Dependencies

	public userSessionService = inject(UserSessionService);
	private bootstrapService = inject(BootstrapService);

	public AuditEntityType = AuditEntityType;

	//#endregion Dependencies

	//#region Category Sections

	// Fixed display order, independent of any per-section sort setting.
	public readonly categories: ChartOfAccountCategory[] = [
		ChartOfAccountCategory.Assets,
		ChartOfAccountCategory.AssetsCash,
		ChartOfAccountCategory.Liabilities,
		ChartOfAccountCategory.Equity,
		ChartOfAccountCategory.Revenue,
		ChartOfAccountCategory.Expenses
	];

	public organizationId = computed(() => this.userSessionService.activeOrganization()?.organizationId);

	//#endregion Category Sections

	//#region Filter Bar

	public searchTermInput = '';
	public appliedSearchTerm = '';

	public statusFilter: 'active' | 'retired' | 'all' = 'active';

	public sortOptions = ListConstants.CHART_OF_ACCOUNTS_SORT_OPTIONS();
	public orderBy: string = ListConstants.CHART_OF_ACCOUNTS_ORDERBY_DEFAULT();

	public get isActiveFilterValue(): boolean | undefined {
		switch (this.statusFilter) {
			case 'active': return true;
			case 'retired': return false;
			default: return undefined;
		}
	}

	public applySearch(): void {
		this.appliedSearchTerm = this.searchTermInput.trim();
	}

	public clearSearch(): void {
		this.searchTermInput = '';
		this.appliedSearchTerm = '';
	}

	public setSortOrder(value: string): void {
		this.orderBy = value;
	}

	//#endregion Filter Bar

	//#region Activity Log

	public isActivityLogOpen = signal<boolean>(false);

	public openActivityLog(): void {
		// Gated behind isActivityLogOpen so ms-audit-logs is destroyed/recreated on each open,
		// forcing its constructor effect to re-fetch instead of showing stale, page-load-time data.
		this.isActivityLogOpen.set(true);
		this.bootstrapService.manageModal('#coaActivityLogModal', ModalActions.Show);
	}

	public closeActivityLog(): void {
		this.isActivityLogOpen.set(false);
		this.bootstrapService.manageModal('#coaActivityLogModal', ModalActions.Hide);
	}

	//#endregion Activity Log
}
